// hh-345 — переиспользуемый пикер иконки.
//
// Показывает текущую иконку сущности и позволяет:
//  - открыть сетку иконок из ICON_NAMES и выбрать любую вручную;
//  - вернуть режим «авто» (сброс ручного выбора в null).
//
// Контракт значения совпадает с полем `icon` API: строка = ручная иконка,
// null = авто-подбор. Авто-подобранная иконка показывается, когда value=null —
// её вычисляет вызывающий код и передаёт через `autoIcon`.

import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { T, Icon, Chip, ICON_NAMES } from '../../tokens';
import type { IconName } from '../../tokens';
import { FormLabel } from './formPrimitives';

export interface IconPickerProps {
  /** Текущее значение поля `icon`: имя иконки (ручной выбор) или null (авто). */
  readonly value: string | null;
  /** onChange(null) — сброс в авто; onChange(name) — ручной выбор. */
  readonly onChange: (icon: string | null) => void;
  /** Иконка, подобранная автоматически — показывается, когда value === null. */
  readonly autoIcon: IconName;
  readonly disabled?: boolean;
  /** Подпись над контролом. По умолчанию «Иконка». */
  readonly label?: string;
}

export function IconPicker({
  value,
  onChange,
  autoIcon,
  disabled = false,
  label = 'Иконка',
}: IconPickerProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);

  const isAuto = value === null;
  // Иконка для превью: ручная (если валидна) либо авто-подобранная.
  const previewIcon: IconName = isAuto
    ? autoIcon
    : ICON_NAMES.includes(value as IconName)
      ? (value as IconName)
      : autoIcon;

  const pick = (name: IconName): void => {
    onChange(name);
    setOpen(false);
  };

  const resetAuto = (): void => {
    onChange(null);
    setOpen(false);
  };

  return (
    <div>
      <FormLabel>{label}</FormLabel>
      {/* Сама иконка — главный элемент. Тап — раскрыть сетку. */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          padding: '12px',
          background: T.surface,
          border: `1px solid ${open ? T.brandSoft2 : T.border}`,
          borderRadius: 12,
          fontFamily: T.font,
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          textAlign: 'left',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: T.brandSoft,
            border: `1px solid ${T.brandSoft2}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name={previewIcon} size={32} color={T.brandDeep} />
        </div>
        {isAuto && (
          <Chip tone="brand" icon="spark">
            авто
          </Chip>
        )}
        <Icon
          name={open ? 'chevD' : 'chevR'}
          size={18}
          color={T.ink3}
          style={{ marginLeft: 'auto' }}
        />
      </button>

      {open && (
        <div
          style={{
            marginTop: 8,
            padding: 12,
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 12,
          }}
        >
          {/* Кнопка «Авто» — сброс ручного выбора. */}
          <button
            type="button"
            disabled={disabled}
            onClick={resetAuto}
            style={{
              width: '100%',
              padding: '8px 12px',
              marginBottom: 10,
              background: isAuto ? T.brandSoft : T.bg,
              border: `1px solid ${isAuto ? T.brandSoft2 : T.border}`,
              borderRadius: 10,
              fontFamily: T.font,
              fontSize: 13,
              fontWeight: 650,
              color: isAuto ? T.brandDeep : T.ink,
              cursor: disabled ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="spark" size={15} color={isAuto ? T.brandDeep : T.ink3} />
            Авто — подбирать по названию
          </button>

          {/* Сетка всех доступных иконок. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 6,
            }}
          >
            {ICON_NAMES.map((name) => {
              const selected = !isAuto && value === name;
              return (
                <button
                  key={name}
                  type="button"
                  disabled={disabled}
                  title={name}
                  aria-label={name}
                  onClick={() => pick(name)}
                  style={{
                    aspectRatio: '1',
                    background: selected ? T.brand : T.bg,
                    border: `1px solid ${selected ? T.brand : T.border}`,
                    borderRadius: 10,
                    cursor: disabled ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                  }}
                >
                  <Icon
                    name={name}
                    size={20}
                    color={selected ? '#fff' : T.ink2}
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
